import React, { useEffect, useRef, useState } from 'react';
import { CopyButton } from '../debug/CopyButton';

/**
 * NetTrafficOverlayContent — inner content of the NET TRAFFIC debug tool.
 *
 * Reads window.nm.tracer.recentTraffic on the 250ms tick, dedupes by seq,
 * and renders cumulative msgs/bytes in+out + per-sec rates + top-3
 * message types per direction. Complements NetobsPacketSparkline
 * (30s window) with session-cumulative numbers.
 *
 * Visibility is no longer controlled inside this component — it always
 * renders when mounted. DebugHUDHost decides when to mount it (driven by
 * DebugToolsContext + DebugLauncher).
 */
const POLL_INTERVAL_MS = 250;

interface TrafficEvent {
  seq?: number;
  timestamp: string;
  direction: string;     // 'client->server' | 'server->client'
  type: string;
  bytes: number;
}

interface Totals {
  msgsIn: number;
  msgsOut: number;
  bytesIn: number;
  bytesOut: number;
  msgsInPerSec: number;
  msgsOutPerSec: number;
  bytesInPerSec: number;
  bytesOutPerSec: number;
  uptimeSec: number;
  topInTypes: [string, number][];
  topOutTypes: [string, number][];
}

const ZERO: Totals = {
  msgsIn: 0, msgsOut: 0, bytesIn: 0, bytesOut: 0,
  msgsInPerSec: 0, msgsOutPerSec: 0, bytesInPerSec: 0, bytesOutPerSec: 0,
  uptimeSec: 0, topInTypes: [], topOutTypes: [],
};

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtPerSec(n: number, unit: string): string {
  if (n === 0) return `0 ${unit}/s`;
  if (unit === 'B' && n >= 1024) return `${(n / 1024).toFixed(1)} KB/s`;
  return `${n.toFixed(unit === 'B' ? 0 : 1)} ${unit}/s`;
}

export const NetTrafficOverlayContent: React.FC = () => {
  const [totals, setTotals] = useState<Totals>(ZERO);

  // Refs persist across re-renders. We dedupe events by seq so we count each
  // tracer event once even though it stays in recentTraffic until the ring
  // wraps. Tracer ring is 300 events (per useNetobsTraffic comment).
  const seenSeqRef = useRef<Set<number>>(new Set());
  const cumulativeRef = useRef({ msgsIn: 0, msgsOut: 0, bytesIn: 0, bytesOut: 0 });
  const typeCountInRef = useRef<Map<string, number>>(new Map());
  const typeCountOutRef = useRef<Map<string, number>>(new Map());
  const startTimeRef = useRef<number>(0);
  const lastTickTotalsRef = useRef({ msgsIn: 0, msgsOut: 0, bytesIn: 0, bytesOut: 0, t: 0 });

  useEffect(() => {
    startTimeRef.current = Date.now();
    let synthSeq = -1;  // for events without seq

    const tick = () => {
      const tracer = (window as any).nm?.tracer;
      if (!tracer || !Array.isArray(tracer.recentTraffic)) {
        setTotals((prev) => prev.msgsIn + prev.msgsOut > 0 ? prev : ZERO);
        return;
      }
      const events = tracer.recentTraffic as TrafficEvent[];
      const seen = seenSeqRef.current;
      const cum = cumulativeRef.current;
      const tIn = typeCountInRef.current;
      const tOut = typeCountOutRef.current;

      for (const ev of events) {
        const key = typeof ev.seq === 'number' ? ev.seq : (synthSeq--);
        if (seen.has(key)) continue;
        seen.add(key);
        const isOut = ev.direction === 'client->server';
        if (isOut) {
          cum.msgsOut += 1;
          cum.bytesOut += ev.bytes || 0;
          tOut.set(ev.type, (tOut.get(ev.type) || 0) + 1);
        } else {
          cum.msgsIn += 1;
          cum.bytesIn += ev.bytes || 0;
          tIn.set(ev.type, (tIn.get(ev.type) || 0) + 1);
        }
      }

      // Trim seen set if it gets large (keep last 5000 seqs)
      if (seen.size > 5000) {
        const arr = Array.from(seen).slice(-2500);
        seenSeqRef.current = new Set(arr);
      }

      const now = Date.now();
      const last = lastTickTotalsRef.current;
      const dtSec = last.t > 0 ? (now - last.t) / 1000 : 0;
      const rates = dtSec > 0 ? {
        msgsInPerSec: (cum.msgsIn - last.msgsIn) / dtSec,
        msgsOutPerSec: (cum.msgsOut - last.msgsOut) / dtSec,
        bytesInPerSec: (cum.bytesIn - last.bytesIn) / dtSec,
        bytesOutPerSec: (cum.bytesOut - last.bytesOut) / dtSec,
      } : { msgsInPerSec: 0, msgsOutPerSec: 0, bytesInPerSec: 0, bytesOutPerSec: 0 };
      lastTickTotalsRef.current = { msgsIn: cum.msgsIn, msgsOut: cum.msgsOut, bytesIn: cum.bytesIn, bytesOut: cum.bytesOut, t: now };

      const topIn = Array.from(tIn.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3);
      const topOut = Array.from(tOut.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3);

      setTotals({
        msgsIn: cum.msgsIn,
        msgsOut: cum.msgsOut,
        bytesIn: cum.bytesIn,
        bytesOut: cum.bytesOut,
        ...rates,
        uptimeSec: (now - startTimeRef.current) / 1000,
        topInTypes: topIn,
        topOutTypes: topOut,
      });
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const ratio = totals.msgsOut > 0 ? (totals.msgsIn / totals.msgsOut).toFixed(1) : '∞';

  return (
    <div
      data-testid="net-traffic-overlay"
      style={{
        color: '#a8e6a8',
        fontFamily: 'monospace',
        fontSize: 10,
        lineHeight: 1.35,
        padding: '4px 6px',
        userSelect: 'none',
      }}
    >
      <div style={{ color: '#7af', marginBottom: 2, letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>NET TRAFFIC (since mount)</span>
        <CopyButton getText={() => [
          `NET TRAFFIC @ ${new Date().toISOString()}`,
          `uptime:    ${totals.uptimeSec.toFixed(1)}s`,
          `↓ in:      ${totals.msgsIn} msgs · ${fmtBytes(totals.bytesIn)}`,
          `↓ rate:    ${fmtPerSec(totals.msgsInPerSec, 'msg')} · ${fmtPerSec(totals.bytesInPerSec, 'B')}`,
          `↑ out:     ${totals.msgsOut} msgs · ${fmtBytes(totals.bytesOut)}`,
          `↑ rate:    ${fmtPerSec(totals.msgsOutPerSec, 'msg')} · ${fmtPerSec(totals.bytesOutPerSec, 'B')}`,
          `in:out:    ${ratio}`,
          ...(totals.topInTypes.length ? ['top in:', ...totals.topInTypes.map(([t, c]) => `  ↓ ${t} ×${c}`)] : []),
          ...(totals.topOutTypes.length ? ['top out:', ...totals.topOutTypes.map(([t, c]) => `  ↑ ${t} ×${c}`)] : []),
        ].join('\n')} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 6 }}>
        <span style={{ color: '#888' }}>uptime</span><span>{totals.uptimeSec.toFixed(1)}s</span>

        <span style={{ color: '#5af' }}>↓ in</span>
        <span>{totals.msgsIn} msgs · {fmtBytes(totals.bytesIn)}</span>

        <span style={{ color: '#5af' }}>↓ rate</span>
        <span>{fmtPerSec(totals.msgsInPerSec, 'msg')} · {fmtPerSec(totals.bytesInPerSec, 'B')}</span>

        <span style={{ color: '#fa5' }}>↑ out</span>
        <span>{totals.msgsOut} msgs · {fmtBytes(totals.bytesOut)}</span>

        <span style={{ color: '#fa5' }}>↑ rate</span>
        <span>{fmtPerSec(totals.msgsOutPerSec, 'msg')} · {fmtPerSec(totals.bytesOutPerSec, 'B')}</span>

        <span style={{ color: '#888' }}>in:out</span><span>{ratio}</span>
      </div>

      {(totals.topInTypes.length > 0 || totals.topOutTypes.length > 0) && (
        <>
          <div style={{ color: '#7af', margin: '4px 0 2px', letterSpacing: '0.05em', fontSize: 9 }}>TOP TYPES</div>
          {totals.topInTypes.map(([t, c]) => (
            <div key={`in-${t}`} style={{ fontSize: 9 }}>
              <span style={{ color: '#5af' }}>↓</span> <span style={{ color: '#bbb' }}>{t.slice(0, 22)}</span> <span style={{ color: '#666' }}>×{c}</span>
            </div>
          ))}
          {totals.topOutTypes.map(([t, c]) => (
            <div key={`out-${t}`} style={{ fontSize: 9 }}>
              <span style={{ color: '#fa5' }}>↑</span> <span style={{ color: '#bbb' }}>{t.slice(0, 22)}</span> <span style={{ color: '#666' }}>×{c}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
};
